import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import {
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  Link,
  Progress,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { Button, Colors, Icon } from '@mercator-gcp/grout';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import Table from 'components/layout/Table';
import { useInvalidateGroupConfigurations } from 'hooks/useGroupConfig';
import { useToast } from 'hooks/useToast';
import {
  trackImportCompanies,
  ImportCompaniesSuccessStates,
} from 'services/analytics/trackImportCompanies';
import CompaniesAPI from 'services/API/CompaniesAPI';
import GroupConfigurationAPI from 'services/API/GroupConfigurationAPI';
import { useUser } from 'state/auth';
import { useRefetchGroups } from 'state/refetchGroups';
import {
  CompanyGroupsPageViewSections,
  CompanyGroupsPageViewSubSections,
} from 'utils/analytics/constants';
import { GROUP_TYPE } from 'utils/constants';
import { buildDashboardURLWithParams } from 'utils/navigation';

const MAX_CSV_ROWS = 500;
const BATCH_SIZE = 20;

type CsvRow = { name: string; website: string };
type MatchRow = {
  csv_name: string;
  csv_website: string;
  company_id: number;
  company_name: string;
  company_website: string;
  company_uuid: string | null;
};
type UnmatchedRow = { csv_name: string; csv_website: string };

function parseCsvFile(file: File): Promise<CsvRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        resolve([]);

        return;
      }
      const rows: CsvRow[] = [];
      for (let i = 1; i < lines.length; i += 1) {
        if (rows.length >= MAX_CSV_ROWS) break;
        const cells = lines[i].split(',').map((c) => c.trim());
        const name = (cells[0] ?? '').trim();
        const website = (cells[1] ?? '').trim();
        if (name || website) rows.push({ name, website });
      }
      resolve(rows);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function downloadSampleCsv() {
  const csv = 'Company Name,Company URL\nExample Inc,https://example.com';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sample-company-import.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return '<1 MB';

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Props = {
  groupId: string;
  groupName: string;
};

const ImportCompaniesFlow = ({ groupId, groupName }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [unmatched, setUnmatched] = useState<UnmatchedRow[]>([]);
  const [matching, setMatching] = useState(false);
  const [matchProgress, setMatchProgress] = useState<{
    processed: number;
    total: number;
  } | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { renderToast } = useToast();
  const navigate = useNavigate();
  const { user } = useUser();
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();
  const { updateRefetchGroups } = useRefetchGroups();

  const onFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setError(null);
    setMatches([]);
    setUnmatched([]);
    if (!f) {
      setFile(null);
      setRows([]);

      return;
    }
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file.');
      setFile(null);
      setRows([]);

      return;
    }
    setFile(f);
    parseCsvFile(f)
      .then((parsed) => {
        setRows(parsed);
        if (parsed.length === 0) setError('No rows with at least NAME or WEBSITE found.');
        else if (parsed.length >= MAX_CSV_ROWS)
          setError(
            `Only the first ${MAX_CSV_ROWS} rows are processed. Split larger files.`,
          );
      })
      .catch((err) => {
        setError(err?.message || 'Failed to parse CSV.');
        setRows([]);
      });
  }, []);

  const runMatch = useCallback(async () => {
    if (rows.length === 0) return;
    setMatching(true);
    setMatchProgress({ processed: 0, total: rows.length });
    setError(null);
    setMatches([]);
    setUnmatched([]);

    let matchesAccum: MatchRow[] = [];
    let unmatchedAccum: UnmatchedRow[] = [];

    try {
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        /* eslint-disable no-await-in-loop */
        const { data } = await CompaniesAPI.postMatchCompanies(batch);
        const processed = Math.min(i + batch.length, rows.length);
        setMatchProgress({ processed, total: rows.length });

        if (data?.matches?.length) {
          matchesAccum = [...matchesAccum, ...data.matches];
          setMatches(matchesAccum);
        }
        if (data?.unmatched?.length) {
          unmatchedAccum = [...unmatchedAccum, ...data.unmatched];
          setUnmatched(unmatchedAccum);
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Match request failed.';
      setError(msg);
      setMatches([]);
      setUnmatched([]);
    } finally {
      setMatching(false);
      setMatchProgress(null);
    }
  }, [rows]);

  const addToGroup = useCallback(async () => {
    const companyIds = Array.from(new Set(matches.map((m) => m.company_id)));
    if (companyIds.length === 0) {
      renderToast({
        toastTitle: 'add_companies_to_group',
        status: 'warning',
        label: 'No matched companies to add.',
      });

      return;
    }
    const groupConfigId = Number(groupId);
    if (!groupConfigId) {
      renderToast({
        toastTitle: 'add_companies_to_group',
        status: 'error',
        title: 'Error',
        label: 'Invalid group.',
      });

      return;
    }
    setAdding(true);
    setError(null);
    try {
      const res = await GroupConfigurationAPI.postAddCompaniesToGroupConfig(
        groupConfigId,
        companyIds,
      );
      const added = (res?.data as { added?: number })?.added ?? 0;
      const alreadyInGroup =
        (res?.data as { already_in_group?: number })?.already_in_group ?? 0;
      const addedPart = added === 1 ? '1 company added' : `${added} companies added`;
      const label =
        alreadyInGroup > 0
          ? `${addedPart}, ${
              alreadyInGroup === 1
                ? '1 company already in group'
                : `${alreadyInGroup} companies already in group`
            }.`
          : `${addedPart}.`;
      renderToast({
        toastTitle: 'add_companies_to_group',
        status: 'success',
        title: 'Companies added to group',
        label,
      });
      trackImportCompanies({
        group_id: String(groupId),
        section_name: CompanyGroupsPageViewSections.COMPANY_GROUPS,
        sub_section_name: CompanyGroupsPageViewSubSections.IMPORT,
        click_text: 'Add companies to group',
        success_state: ImportCompaniesSuccessStates.SUCCESS,
      });
      invalidateGroupConfigurations(String(user?.id), GROUP_TYPE.COMPANY);
      updateRefetchGroups((prev) => prev + 1);
      navigate(
        buildDashboardURLWithParams({
          groupId: groupConfigId,
          groupType: GROUP_TYPE.COMPANY,
        }),
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response
          ?.data?.error ||
        (err as { message?: string })?.message ||
        'Failed to add companies.';
      setError(msg);
      renderToast({
        toastTitle: 'add_companies_to_group_error',
        status: 'error',
        title: 'Error',
        label: msg,
      });
    } finally {
      setAdding(false);
    }
  }, [
    groupId,
    matches,
    user?.id,
    navigate,
    renderToast,
    invalidateGroupConfigurations,
    updateRefetchGroups,
  ]);

  const clearAll = useCallback(() => {
    setFile(null);
    setRows([]);
    setMatches([]);
    setUnmatched([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeMatch = useCallback((index: number) => {
    setMatches((prev) => {
      const removed = prev[index];
      if (removed) {
        const entry: UnmatchedRow = {
          csv_name: removed.csv_name,
          csv_website: removed.csv_website,
        };
        setUnmatched((u) =>
          u.some(
            (x) => x.csv_website === entry.csv_website && x.csv_name === entry.csv_name,
          )
            ? u
            : [...u, entry],
        );
      }

      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const uniqueCompanyCount =
    matches.length > 0 ? new Set(matches.map((m) => m.company_id)).size : 0;

  const backUrl = buildDashboardURLWithParams({
    groupId: Number(groupId),
    groupType: GROUP_TYPE.COMPANY,
  });

  return (
    <Box p={6} w="1073px" maxW="100%" mx="auto" overflowY="auto">
      <Link
        as={ReactRouterLink}
        to={backUrl}
        color="#76746f"
        _hover={{ textDecoration: 'underline', color: '#1f2b22' }}
        fontSize="13px"
        mb={4}
        display="inline-block"
      >
        ← Back to {groupName}
      </Link>

      <Box mb={2}>
        <Heading
          size="lg"
          color="#1f2b22"
          fontWeight="600"
          fontSize="20px"
          letterSpacing="-0.2px"
          mb={2}
        >
          Import Companies from CSV
        </Heading>
        <Text color="#76746f" fontSize="13px">
          Upload a list of your companies and we will search based on Company Name and
          Company Website URL
        </Text>
      </Box>
      <Box mb={6} />

      <VStack align="stretch" spacing={6}>
        {/* Step 1: Upload CSV */}
        <Box>
          <Heading size="sm" color="#1f2b22" fontSize="16px" fontWeight="600" mb={3}>
            Step 1: Upload CSV
          </Heading>
          {!file ? (
            <Box
              border="1px dashed"
              borderColor="#ccd1cc"
              borderRadius="4px"
              h="175px"
              w="704px"
              maxW="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              position="relative"
              gap={3}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={onFileChange}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
              />
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                w="fit-content"
                minW={0}
              >
                <Icon name={Icon.Name.AddToList} size={80} fill={Colors.ArmyGreen200} />
              </Box>
              <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
                Choose file
              </Button>
              <Text
                color="#76746f"
                fontSize="13px"
                textAlign="center"
                px={4}
                pb={3}
                lineHeight="1.26"
                letterSpacing="-0.13px"
              >
                Columns must be: Company Name, Company URL. Max{' '}
                {MAX_CSV_ROWS.toLocaleString()} rows.
              </Text>
            </Box>
          ) : (
            <Box
              border="1px solid"
              borderColor="#ccd1cc"
              borderRadius="4px"
              maxW="688px"
              p={4}
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={4}
            >
              <VStack align="stretch" gap={2} flex={1} minW={0}>
                <Flex gap={2} align="center">
                  <Box flexShrink={0} w="16px" h="16px">
                    <Icon name={Icon.Name.CircleChecked} size={16} />
                  </Box>
                  <Text
                    fontSize="13px"
                    color="#76746f"
                    lineHeight="1.26"
                    letterSpacing="-0.13px"
                    noOfLines={1}
                  >
                    {file.name}
                  </Text>
                </Flex>
                <Text
                  fontSize="13px"
                  color="#76746f"
                  lineHeight="1.26"
                  letterSpacing="-0.13px"
                >
                  {formatFileSize(file.size)}, {rows.length} rows
                </Text>
              </VStack>
              <IconButton
                aria-label="Remove file"
                variant="ghost"
                size="sm"
                icon={<Icon name={Icon.Name.CloseX} size={16} />}
                onClick={clearAll}
                flexShrink={0}
                w="16px"
                minW="16px"
                h="16px"
              />
            </Box>
          )}
          {!file && (
            <Text color="#76746f" fontSize="13px" mt={3}>
              Not sure where to start?{' '}
              <Button
                variant="link"
                color="#027a3b"
                textDecoration="underline"
                onClick={downloadSampleCsv}
                cursor="pointer"
                fontSize="inherit"
                fontWeight="inherit"
                p={0}
                minW={0}
                h="auto"
                _hover={{ textDecoration: 'underline' }}
              >
                Download Sample CSV File
              </Button>
            </Text>
          )}
          {matching && matchProgress && (
            <Box mt={4} maxW="500px">
              <Progress
                value={
                  matchProgress.total > 0
                    ? (matchProgress.processed / matchProgress.total) * 100
                    : 0
                }
                size="sm"
                colorScheme="green"
                borderRadius="full"
                hasStripe
                isAnimated
              />
              <Text fontSize="xs" color="gray.500" mt={2}>
                {matchProgress.processed} of {matchProgress.total} rows processed
              </Text>
            </Box>
          )}
          {(matching ||
            (rows.length > 0 && matches.length === 0 && unmatched.length === 0)) && (
            <Button
              variant="primary"
              onClick={runMatch}
              isDisabled={matching || rows.length === 0}
              isLoading={matching}
              mt={4}
            >
              Match companies
            </Button>
          )}
        </Box>

        {/* Step 2: Add to group (2 steps total: Upload, then Match & Add) */}
        {(matches.length > 0 || unmatched.length > 0) && (
          <>
            <Divider />
            <Box>
              <Flex justify="space-between" align="center" gap={4} mb={3} wrap="wrap">
                <Heading
                  size="sm"
                  color="#1f2b22"
                  fontSize="16px"
                  fontWeight="600"
                  mb={0}
                >
                  Step 2: Add to group
                </Heading>
                {matches.length > 0 && (
                  <Button
                    variant="primary"
                    onClick={addToGroup}
                    isLoading={adding}
                    isDisabled={matching}
                    flexShrink={0}
                  >
                    Add companies to group
                  </Button>
                )}
              </Flex>
              {matches.length === 0 && (
                <Text color="#76746f" fontSize="sm" py={4}>
                  No companies matched
                </Text>
              )}
              {matches.length > 0 && (
                <Text fontSize="sm" color="#76746f" mb={2}>
                  {uniqueCompanyCount} unique companies will be added to &quot;{groupName}
                  &quot;
                  {uniqueCompanyCount < matches.length &&
                    ` (${
                      matches.length - uniqueCompanyCount
                    } CSV row(s) matched the same company)`}
                </Text>
              )}
              {matches.length > 0 && (
                <TableContainer overflow="visible" overflowX="visible" maxW="100%">
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>CSV name</Th>
                        <Th>CSV website</Th>
                        <Th>Matched company</Th>
                        <Th>Company website</Th>
                        <Th>Profile</Th>
                        <Th w="80px" />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {matches.map((m, i) => (
                        <Tr key={`match-${m.csv_website}-${m.company_id}-${m.csv_name}`}>
                          <Td>{m.csv_name || '—'}</Td>
                          <Td>{m.csv_website}</Td>
                          <Td>{m.company_name}</Td>
                          <Td>{m.company_website || '—'}</Td>
                          <Td>
                            {m.company_uuid ? (
                              <Link
                                as={ReactRouterLink}
                                to={`/companies/${m.company_uuid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="blue.600"
                                _hover={{ textDecoration: 'underline' }}
                              >
                                View profile
                              </Link>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td>
                            <Button
                              variant="alert"
                              colorScheme="red"
                              onClick={() => removeMatch(i)}
                            >
                              Remove
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              {unmatched.length > 0 && (
                <>
                  <Heading size="sm" mt={4} mb={2}>
                    Unmatched ({unmatched.length})
                  </Heading>
                  <Text fontSize="sm" color={Colors.TextQuiet} mb={2}>
                    We couldn&apos;t find a 100% certain match for these records. To avoid
                    false positives, we look for exact name matches. To increase your
                    match rate, include Company URLs in your CSV.
                  </Text>
                  <TableContainer overflow="visible" overflowX="visible" maxW="100%">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>CSV name</Th>
                          <Th>CSV website</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {unmatched.map((u) => (
                          <Tr key={`unmatched-${u.csv_name}-${u.csv_website}`}>
                            <Td>{u.csv_name || '—'}</Td>
                            <Td>{u.csv_website}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          </>
        )}

        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default ImportCompaniesFlow;
